import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "accent" | "secondary" | "outline" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: string;
  children?: ReactNode;
}

const variants: Record<Variant, string> = {
  primary:
    "bg-primary-600 text-white border border-primary-600 shadow-card hover:bg-primary-700 hover:border-primary-700",
  accent: "bg-accent-600 text-white border border-accent-600 shadow-card hover:bg-accent-700 hover:border-accent-700",
  secondary: "bg-primary-50 text-primary-800 border border-primary-100 hover:bg-primary-100",
  outline:
    "bg-background-50 text-foreground-800 border border-background-300 shadow-card hover:bg-background-100 hover:border-background-400",
  ghost: "bg-transparent text-foreground-700 border border-transparent hover:bg-background-200/70",
  danger: "bg-background-50 text-accent-700 border border-accent-200 hover:bg-accent-50 hover:border-accent-300",
};

// Au doigt, les boutons gardent une zone de toucher d'au moins 40 px (44 px pour la taille normale).
const sizes: Record<Size, string> = {
  sm: "text-xs px-3 gap-1.5 min-h-[2.5rem] sm:min-h-[2rem]",
  md: "text-sm px-4 gap-2 min-h-[2.75rem] sm:min-h-[2.25rem]",
  lg: "text-sm px-5 gap-2 min-h-[2.75rem] sm:min-h-[2.5rem]",
};

/** Bouton de l'application : s'enfonce à l'appui, contour visible au clavier. */
export default function Button({
  variant = "primary",
  size = "md",
  icon,
  children,
  className = "",
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      {...rest}
      className={`press inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {icon && (
        <span className="w-4 h-4 flex items-center justify-center shrink-0" aria-hidden="true">
          <i className={`${icon} text-[1rem] leading-none`}></i>
        </span>
      )}
      {children}
    </button>
  );
}
